// src/hooks/useWorkFilters.js
// Single Source of Truth for Work filters (Calendar + List)
// - Canonical schema (UI → hooks → services):
//   { q, status[], priority[], type[], from, to, assigneeId[], clientId[], includeCancelled }
// ... (header unchanged)

import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

/** Environment flag for ad-hoc debugging */
const DEBUG = String(process.env.REACT_APP_WORK_FILTERS_DEBUG || '').toLowerCase() === 'true';

/** Canonical defaults */
const DEFAULTS = Object.freeze({
  q: null,
  status: null,
  priority: null,
  type: null,
  from: null,
  to: null,
  assigneeId: null,
  clientId: null,
  includeCancelled: false,
});

/** Keys that are arrays in canonical schema */
const ARRAY_KEYS = new Set(['status', 'priority', 'type', 'assigneeId', 'clientId']);

/** URL param names (canonical) */
const PARAMS = Object.freeze({
  q: 'q',
  status: 'status',
  priority: 'priority',
  type: 'type',
  from: 'from',
  to: 'to',
  assigneeId: 'assigneeId',
  clientId: 'clientId',
  includeCancelled: 'includeCancelled',
});

/** Legacy/alias params we will accept for back-compat (mapped → canonical) */
const ALIASES = Object.freeze({
  title: 'q',
  search: 'q',
  query: 'q',
  keyword: 'q',
  term: 'q',
  assignee: 'assigneeId',
  assigneeIds: 'assigneeId',
  client: 'clientId',
  clientIds: 'clientId',
  statuses: 'status',
  priorities: 'priority',
  types: 'type',
  include_canceled: 'includeCancelled',
  // excludeCancelled is handled separately (inverse semantic)
});

/* ───────────────────────── helpers ───────────────────────── */
const isNonEmpty = (v) => v != null && String(v).trim() !== '';

function parseCSV(value) {
  if (!isNonEmpty(value)) return null;
  const arr = String(value)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
  return arr.length ? arr : null;
}

function normEnumArray(arr) {
  if (!arr || (Array.isArray(arr) && arr.length === 0)) return null;
  const list = Array.isArray(arr) ? arr : [arr];
  const out = Array.from(
    new Set(
      list
        .map((x) => (x == null ? '' : String(x).trim()))
        .filter(Boolean)
        .map((x) => x.toLowerCase())
    )
  ).sort();
  return out.length ? out : null;
}

function normIdArray(arr) {
  if (!arr || (Array.isArray(arr) && arr.length === 0)) return null;
  const list = Array.isArray(arr) ? arr : [arr];
  const out = Array.from(
    new Set(list.map((x) => (x == null ? '' : String(x).trim())).filter(Boolean))
  );
  return out.length ? out : null;
}

function normScalar(val) {
  if (!isNonEmpty(val)) return null;
  return String(val).trim();
}

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
function normDate(val) {
  const s = normScalar(val);
  return s && DATE_RE.test(s) ? s : null;
}

function normBoolean(val) {
  if (val === true || val === false) return val;
  if (!isNonEmpty(val)) return false;
  const s = String(val).trim().toLowerCase();
  return s === '1' || s === 'true' || s === 'yes' || s === 'y';
}

/* ───────────────────────── parsing/serialization ───────────────────────── */
function readFromSearchParams(sp) {
  const bag = {};
  for (const [k, v] of sp.entries()) {
    const canon = PARAMS[k] ? k : (ALIASES[k] || null);
    if (!canon) continue;
    if (ARRAY_KEYS.has(canon)) {
      const existing = bag[canon] || [];
      const chunk = parseCSV(v) || [v];
      bag[canon] = existing.concat(chunk);
    } else {
      bag[canon] = v;
    }
  }

  const filters = {
    q: normScalar(bag.q),
    status: normEnumArray(bag.status),
    priority: normEnumArray(bag.priority),
    type: normEnumArray(bag.type),
    from: normDate(bag.from),
    to: normDate(bag.to),
    assigneeId: normIdArray(bag.assigneeId),
    clientId: normIdArray(bag.clientId),
    includeCancelled: normBoolean(bag.includeCancelled),
  };

  if (sp.has('excludeCancelled')) {
    const ex = normBoolean(sp.get('excludeCancelled'));
    if (ex === true && !sp.has('includeCancelled')) {
      filters.includeCancelled = false;
    }
  }

  return withDefaults(filters);
}

function withDefaults(partial) {
  return {
    q: partial.q ?? DEFAULTS.q,
    status: partial.status ?? DEFAULTS.status,
    priority: partial.priority ?? DEFAULTS.priority,
    type: partial.type ?? DEFAULTS.type,
    from: partial.from ?? DEFAULTS.from,
    to: partial.to ?? DEFAULTS.to,
    assigneeId: partial.assigneeId ?? DEFAULTS.assigneeId,
    clientId: partial.clientId ?? DEFAULTS.clientId,
    includeCancelled: Boolean(partial.includeCancelled ?? DEFAULTS.includeCancelled),
  };
}

/** ✅ UPDATED: normalizePatch now accepts aliases (search/title/query/…) and maps to canonical keys */
function normalizePatch(patch) {
  if (!patch || typeof patch !== 'object') return {};
  const out = {};

  // Map each incoming key to its canonical name
  for (const [rawKey, rawVal] of Object.entries(patch)) {
    const key = PARAMS[rawKey] ? rawKey : (ALIASES[rawKey] || null);
    if (!key) continue;

    switch (key) {
      case 'q': {
        const v =
          // support alias bags like { q } or { search } or even { q: '', search: 'X' }
          normScalar(
            isNonEmpty(rawVal)
              ? rawVal
              : isNonEmpty(patch.q) ? patch.q
              : isNonEmpty(patch.search) ? patch.search
              : isNonEmpty(patch.title) ? patch.title
              : isNonEmpty(patch.query) ? patch.query
              : isNonEmpty(patch.keyword) ? patch.keyword
              : isNonEmpty(patch.term) ? patch.term
              : null
          );
        out.q = v;
        break;
      }

      case 'status':
      case 'priority':
      case 'type': {
        out[key] = normEnumArray(rawVal);
        break;
      }

      case 'assigneeId':
      case 'clientId': {
        out[key] = normIdArray(rawVal);
        break;
      }

      case 'from':
      case 'to': {
        out[key] = normDate(rawVal);
        break;
      }

      case 'includeCancelled': {
        out.includeCancelled = normBoolean(rawVal);
        break;
      }

      default:
        break;
    }
  }

  return out;
}

function writeToSearchParams(filters) {
  const sp = new URLSearchParams();

  if (isNonEmpty(filters.q)) sp.set(PARAMS.q, String(filters.q));

  for (const k of ['status', 'priority', 'type']) {
    const arr = normEnumArray(filters[k]);
    if (arr) sp.set(PARAMS[k], arr.join(','));
  }
  for (const k of ['assigneeId', 'clientId']) {
    const arr = normIdArray(filters[k]);
    if (arr) sp.set(PARAMS[k], arr.join(','));
  }

  if (isNonEmpty(filters.from)) sp.set(PARAMS.from, String(filters.from));
  if (isNonEmpty(filters.to)) sp.set(PARAMS.to, String(filters.to));

  if (filters.includeCancelled === true) sp.set(PARAMS.includeCancelled, 'true');

  return sp;
}

function buildKeyPart(filters) {
  const out = {};
  if (isNonEmpty(filters.q)) out.q = filters.q;

  for (const k of ['status', 'priority', 'type']) {
    const arr = normEnumArray(filters[k]);
    if (arr) out[k] = arr;
  }
  for (const k of ['assigneeId', 'clientId']) {
    const arr = normIdArray(filters[k]);
    if (arr) out[k] = arr;
  }

  if (isNonEmpty(filters.from)) out.from = filters.from;
  if (isNonEmpty(filters.to)) out.to = filters.to;

  if (filters.includeCancelled === true) out.includeCancelled = true;

  return out;
}

function stableString(obj) {
  return JSON.stringify(obj, Object.keys(obj).sort());
}

/** Public hook */
export default function useWorkFilters(options = {}) {
  const { replaceHistory = true } = options;
  const [searchParams, setSearchParams] = useSearchParams();

  const filters = useMemo(() => {
    const f = readFromSearchParams(searchParams);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[workFilters:read]', stableString(buildKeyPart(f)));
    }
    return f;
  }, [searchParams]);

  const keyPart = useMemo(() => buildKeyPart(filters), [filters]);

  const setFilters = useCallback(
    (patch) => {
      const normalizedPatch = normalizePatch(patch);
      const merged = withDefaults({ ...filters, ...normalizedPatch });

      const nextSP = writeToSearchParams(merged);

      if (DEBUG) {
        // eslint-disable-next-line no-console
        console.log('[workFilters:update]', {
          patch: normalizedPatch,
          next: stableString(buildKeyPart(merged)),
        });
      }

      setSearchParams(nextSP, { replace: replaceHistory });
    },
    [filters, replaceHistory, setSearchParams]
  );

  const resetFilters = useCallback(() => {
    const nextSP = writeToSearchParams(DEFAULTS);
    if (DEBUG) {
      // eslint-disable-next-line no-console
      console.log('[workFilters:reset]');
    }
    setSearchParams(nextSP, { replace: replaceHistory });
  }, [replaceHistory, setSearchParams]);

  const buildKey = useCallback(
    (prefix) => {
      const base = Array.isArray(prefix) ? prefix : [prefix];
      return [...base, keyPart];
    },
    [keyPart]
  );

  return {
    filters,
    setFilters,
    resetFilters,
    keyPart,
    buildKey,
  };
}
